import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LogoInline } from '../LogoInline.tsx';
import { PillButton } from '../PillButton.tsx';
import { EYEBROW } from './cmsTheme.ts';

export function Footer() {
  return (
    <footer className="bg-mf-greenDeep text-mf-yellow relative overflow-hidden py-10 md:py-20">
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-8 md:gap-10">
        <div className="md:col-span-5">
          <div><LogoInline className="h-7 md:h-9" style={{ color: '#f3ea9a' }} /></div>
          <div className="hidden md:block mt-4 md:mt-5">
            <p className={`${EYEBROW} text-mf-yellow/50 mb-2`}>
              Non-profit - drevet af græsrødder &amp; ildsjæle
            </p>
            <p className="text-mf-yellow/70 max-w-sm text-sm leading-relaxed">
              Foreningen Midtfyns Festival er en non-profit forening, som er sat i verden for at skabe en fynsk folkelig festival for os alle. Vil du være med til at sætte Midtfyn på landkortet og drage folks øjne mod vores egn?
            </p>
          </div>
          <PillButton variant="outlineLight" className="mt-4 md:mt-6" as="a" href="/partnere">
            Sådan støtter du
          </PillButton>
          <SocialLinks />
        </div>

        <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          <FooterCol title="Festival">
            <FooterLink to="/program">Program</FooterLink>
            <FooterLink to="/torsdag">Torsdag</FooterLink>
            <FooterLink to="/camping">Camping &amp; glamping</FooterLink>
          </FooterCol>

          <FooterCol title="Info">
            <FooterLink to="/praktisk">Praktisk info</FooterLink>
            <FooterLink to="/nyheder">Nyheder</FooterLink>
            <FooterLink to="/kontakt">Kontakt</FooterLink>
            <FooterLink to="/presse">Presse</FooterLink>
          </FooterCol>

          <FooterCol title="Bliv en del">
            <FooterLink to="/frivillig">Frivillig</FooterLink>
            <FooterLink href="https://frivillig.midtfynsfestival.dk">Frivillig login</FooterLink>
            <FooterLink to="/foreningen">Bliv medlem</FooterLink>
            <FooterLink to="/ung-i-midten">Ung i Midten</FooterLink>
          </FooterCol>

          <FooterCol title="Foreningen">
            <FooterLink to="/om-midtfyns">Om Midtfyns</FooterLink>
            <FooterLink to="/arrangoergruppen">Arrangørgruppen</FooterLink>
            <FooterLink to="/sekretariatet">Sekretariatet</FooterLink>
            <FooterLink to="/foreningen">Om foreningen</FooterLink>
            <FooterLink to="/partnere">Sponsorer &amp; fonde</FooterLink>
            <FooterLink to="/vedtaegter">Vedtægter</FooterLink>
          </FooterCol>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 mt-8 md:mt-12 pt-5 md:pt-6 border-t border-mf-yellow/15 flex flex-wrap gap-x-6 gap-y-2 text-xs md:text-sm text-mf-yellow/60">
        <div>© Midtfyns Festival · CVR 39609304 · Ringe</div>
        <Link to="/handelsbetingelser" className="hover:text-mf-yellow transition-colors">Handelsbetingelser</Link>
        <Link to="/privatlivspolitik" className="hover:text-mf-yellow transition-colors">Privatlivspolitik</Link>
      </div>
    </footer>
  );
}

// Social profiles for the festival. URLs mirror the ones linked from the current
// midtfynsfestival.dk footer.
const SOCIALS: readonly { readonly label: string; readonly href: string; readonly icon: ReactNode }[] = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/midtfynsfestival',
    icon: <path d="M14 8.5h2V6h-2c-1.66 0-3 1.34-3 3v1.5H9V13h2v5h2.5v-5h1.9l.35-2.5H13.5V9c0-.28.22-.5.5-.5Z" />,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/midtfynsfestival/',
    icon: (
      <>
        <rect x="4.5" y="4.5" width="15" height="15" rx="4.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="12" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="16.4" cy="7.6" r="1.1" />
      </>
    ),
  },
  {
    label: 'LinkedIn',
    href: 'https://dk.linkedin.com/company/midtfyns-festival',
    icon: (
      <>
        <path d="M7.2 9.5H9.4V17H7.2zM8.3 6.3a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6Z" />
        <path d="M11 9.5h2.1v1.03h.03c.29-.53 1-1.1 2.07-1.1 2.2 0 2.6 1.4 2.6 3.3V17h-2.17v-3.4c0-.81-.02-1.85-1.16-1.85-1.16 0-1.34.88-1.34 1.79V17H11z" />
      </>
    ),
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/c/MidtfynsFestival',
    icon: (
      <>
        <path d="M20 9.2a2 2 0 0 0-1.4-1.42C17.4 7.5 12 7.5 12 7.5s-5.4 0-6.6.28A2 2 0 0 0 4 9.2 20.8 20.8 0 0 0 3.8 12 20.8 20.8 0 0 0 4 14.8a2 2 0 0 0 1.4 1.42C6.6 16.5 12 16.5 12 16.5s5.4 0 6.6-.28A2 2 0 0 0 20 14.8 20.8 20.8 0 0 0 20.2 12 20.8 20.8 0 0 0 20 9.2ZM10.5 14.2V9.8l3.8 2.2Z" />
      </>
    ),
  },
];

function SocialLinks() {
  return (
    <div className="mt-6 md:mt-7 flex items-center gap-3">
      {SOCIALS.map(s => (
        <a
          key={s.label}
          href={s.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={s.label}
          className="w-10 h-10 grid place-items-center rounded-full border border-mf-yellow/20 text-mf-yellow/80 hover:text-mf-greenDeep hover:bg-mf-yellow transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>{s.icon}</svg>
        </a>
      ))}
    </div>
  );
}

interface FooterColProps {
  readonly title: string;
  readonly children: ReactNode;
}

function FooterCol({ title, children }: FooterColProps) {
  return (
    <div>
      <div className={`${EYEBROW} text-mf-yellow/60 mb-3 md:mb-4`}>{title}</div>
      <ul className="space-y-1.5 md:space-y-2 text-sm">{children}</ul>
    </div>
  );
}

interface FooterLinkProps {
  readonly children: ReactNode;
  readonly to?: string;
  readonly href?: string;
}

function FooterLink({ children, to = '#', href }: FooterLinkProps) {
  const className = 'text-mf-yellow/85 hover:text-mf-yellow transition-colors';
  return (
    <li>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>
      ) : (
        <Link to={to} className={className}>{children}</Link>
      )}
    </li>
  );
}
